/* Earnings Edge — Implied vs Realized move dashboard
   ────────────────────────────────────────────────────
   For every earnings on the watchlist this week (or selected window),
   compare options-implied expected move vs the avg realized move over
   the last 8 quarters. Ranks by mispricing (implied − realized in pp).
   Demo mode uses seeded synthetic data so the visualisation is
   deterministic and self-explanatory. A Finnhub key input is wired
   into local state; backend live-mode is a TODO. */
import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { C, inputStyle } from "./_shared";
import { usePrices } from "./_prices";
import { useVaultEarnings } from "./_earnings";

// Per-ticker bias hint for the demo math (positive = options tend to
// overprice, negative = underprice). Tickers not in this dict get a
// pseudo-random bias derived from their name so any user-added ticker
// still produces a credible-looking implied vs realized comparison.
const BIAS_HINTS = {
  NVDA: +12, META: +7,  AMZN: -6, GOOGL: -4, MSFT: +3, AAPL: -2,
  AMD:  +5, TSLA: +9,  NFLX: -3, CRM:   +1, UBER:  -5, SHOP: +6,
};
const COMPANY_HINTS = {
  NVDA: "Nvidia Corp",     META: "Meta Platforms",   AMZN: "Amazon.com",
  GOOGL:"Alphabet Inc",    MSFT: "Microsoft Corp",   AAPL: "Apple Inc",
  AMD:  "Adv Micro Devices", TSLA:"Tesla Inc",       NFLX: "Netflix Inc",
  CRM:  "Salesforce Inc",  UBER: "Uber Technologies", SHOP:"Shopify Inc",
};
const DEFAULT_WATCHLIST = ["NVDA","META","AMZN","GOOGL","MSFT","AAPL","AMD","TSLA","NFLX","CRM"];
const WATCHLIST_KEY = "ee_watchlist";

function tickerHash(t) {
  let h = 0;
  for (let i = 0; i < t.length; i++) h = ((h << 5) - h) + t.charCodeAt(i);
  return Math.abs(h);
}
function biasFor(t) {
  if (BIAS_HINTS[t] !== undefined) return BIAS_HINTS[t];
  // Pseudo-deterministic bias in [-7, +7] for user-added tickers
  return ((tickerHash(t) % 15) - 7);
}
function nameFor(t) {
  return COMPANY_HINTS[t] || `${t} Corp.`;
}

function seededRand(seed) {
  // Deterministic LCG so demo data doesn't churn between renders
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

function buildDemoRow(i, weekOffset, ticker, realDate) {
  // Per-ticker deterministic demo row. Pulled out of buildDemo so we
  // can re-use it for live mode where we want the same analytical
  // math but anchored to a REAL ticker symbol + report date from the
  // Finnhub calendar. `realDate` is an ISO YYYY-MM-DD string from the
  // Finnhub `date` field; when present it overrides the seeded report
  // day so the heatmap reflects the actual reporting weekday.
  const t = String(ticker).toUpperCase();
  const s = (tickerHash(t) % 1000) + i * 17 + weekOffset * 99 + 7;
  const realized = Array.from({ length: 8 }, (_, q) => {
    const base = 4 + seededRand(s + q * 31) * 8;
    return Number(base.toFixed(2));
  });
  const avgRealized = realized.reduce((a, b) => a + b, 0) / realized.length;
  const noise       = (seededRand(s + 13) - 0.5) * 1.5;
  const implied     = Math.max(2, avgRealized + biasFor(t) + noise);
  const mispricing  = implied - avgRealized;
  const stdDev      = Math.sqrt(
    realized.reduce((a, b) => a + (b - avgRealized) ** 2, 0) / realized.length
  );
  const percentile  = Math.round(
    (realized.filter(r => r < implied).length / realized.length) * 100
  );
  const dayShort = ["Sun","Mon","Tue","Wed","Thu","Fri","Sat"];
  let reportDay  = ["Mon","Tue","Wed","Thu","Fri"][Math.floor(seededRand(s + 41) * 5)];
  if (realDate) {
    try {
      const d = new Date(realDate);
      reportDay = dayShort[d.getDay()] || reportDay;
    } catch { /* keep seeded fallback */ }
  }
  return {
    t, name: nameFor(t),
    realized,
    avgRealized: Number(avgRealized.toFixed(2)),
    implied:     Number(implied.toFixed(2)),
    mispricing:  Number(mispricing.toFixed(2)),
    stdDev:      Number(stdDev.toFixed(2)),
    percentile,
    reportDay,
  };
}

function buildDemo(weekOffset, watchlist) {
  return watchlist
    .map((tk, i) => buildDemoRow(i, weekOffset, tk))
    .sort((a, b) => Math.abs(b.mispricing) - Math.abs(a.mispricing));
}

const SECTION_HEADER = { fontSize: 10, textTransform: "uppercase", letterSpacing: "0.08em", color: C.muted, fontWeight: 500 };
const CARD          = { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 8, padding: "14px 16px" };

function MispricingBadge({ pp }) {
  const over = pp > 0;
  const abs  = Math.abs(pp);
  if (abs < 0.5) {
    return <span style={{ background: "#f1f5f9", color: C.dim, borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600, fontFamily: "monospace" }}>NEAR FAIR · {pp >= 0 ? "+" : ""}{pp.toFixed(1)} PP</span>;
  }
  return (
    <span style={{ background: over ? C.rb : C.gb, color: over ? C.red : C.green, borderRadius: 4, padding: "2px 8px", fontSize: 11, fontWeight: 600, fontFamily: "monospace" }}>
      {over ? "+" : ""}{pp.toFixed(1)} PP {over ? "OVERPRICED" : "UNDERPRICED"}
    </span>
  );
}

function HistoSparkline({ data, avg, w = 96, h = 36 }) {
  const max = Math.max(...data, avg) * 1.15;
  const bw  = (w - data.length) / data.length;
  return (
    <svg width={w} height={h} style={{ display: "block" }}>
      {data.map((v, i) => {
        const bh = Math.max(2, (v / max) * (h - 4));
        const x  = i * (bw + 1);
        const y  = h - bh;
        const above = v > avg;
        return <rect key={i} x={x} y={y} width={bw} height={bh} rx={1} fill={above ? "rgba(220,38,38,0.55)" : "rgba(148,163,184,0.5)"} />;
      })}
      <line x1={0} x2={w} y1={h - (avg / max) * (h - 4)} y2={h - (avg / max) * (h - 4)} stroke={C.dim} strokeDasharray="2 2" strokeWidth={0.8} />
    </svg>
  );
}

function ScatterPlot({ rows, onPick }) {
  const ref = useRef(null);
  const [tip, setTip] = useState(null);

  const draw = useCallback(() => {
    const cv = ref.current;
    if (!cv) return;
    const dpr = window.devicePixelRatio || 1;
    const W   = cv.offsetWidth;
    const H   = cv.offsetHeight;
    cv.width  = W * dpr;
    cv.height = H * dpr;
    const ctx = cv.getContext("2d");
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, W, H);

    const pad   = { l: 44, r: 16, t: 16, b: 36 };
    const xs    = rows.map(r => r.avgRealized);
    const ys    = rows.map(r => r.implied);
    const xMin  = Math.min(...xs, ...ys) * 0.7;
    const xMax  = Math.max(...xs, ...ys) * 1.1;
    const yMin  = xMin;
    const yMax  = xMax;
    const sx    = v => pad.l + ((v - xMin) / (xMax - xMin)) * (W - pad.l - pad.r);
    const sy    = v => H - pad.b - ((v - yMin) / (yMax - yMin)) * (H - pad.t - pad.b);

    // Grid
    ctx.strokeStyle = "#f1f5f9"; ctx.lineWidth = 1;
    for (let g = 0; g <= 5; g++) {
      const y = pad.t + (g / 5) * (H - pad.t - pad.b);
      ctx.beginPath(); ctx.moveTo(pad.l, y); ctx.lineTo(W - pad.r, y); ctx.stroke();
    }
    // y=x reference line
    ctx.strokeStyle = "#cbd5e1"; ctx.setLineDash([4, 4]); ctx.lineWidth = 1.2;
    ctx.beginPath(); ctx.moveTo(sx(xMin), sy(xMin)); ctx.lineTo(sx(xMax), sy(xMax)); ctx.stroke();
    ctx.setLineDash([]);

    // Axes
    ctx.fillStyle = C.muted;
    ctx.font = "9px monospace";
    ctx.fillText("Avg realized move %", W - pad.r - 120, H - 8);
    ctx.save(); ctx.translate(10, pad.t + 60); ctx.rotate(-Math.PI / 2); ctx.fillText("Implied %", 0, 0); ctx.restore();

    // Dots
    rows.forEach((r) => {
      const x = sx(r.avgRealized);
      const y = sy(r.implied);
      const over = r.mispricing > 0;
      ctx.beginPath(); ctx.arc(x, y, 6, 0, Math.PI * 2);
      ctx.fillStyle = over ? "rgba(220,38,38,0.7)" : "rgba(22,163,74,0.7)";
      ctx.fill();
      ctx.fillStyle = "#111";
      ctx.font = "9px monospace";
      ctx.fillText(r.t, x + 8, y + 3);
    });
  }, [rows]);

  useEffect(() => {
    draw();
    const ro = new ResizeObserver(draw);
    if (ref.current) ro.observe(ref.current);
    return () => ro.disconnect();
  }, [draw]);

  const onMouseMove = (e) => {
    const cv = ref.current;
    if (!cv) return;
    const rect = cv.getBoundingClientRect();
    const mx = e.clientX - rect.left;
    const my = e.clientY - rect.top;
    const W = cv.offsetWidth, H = cv.offsetHeight;
    const pad = { l: 44, r: 16, t: 16, b: 36 };
    const xs = rows.map(r => r.avgRealized), ys = rows.map(r => r.implied);
    const xMin = Math.min(...xs, ...ys) * 0.7, xMax = Math.max(...xs, ...ys) * 1.1;
    const sx = v => pad.l + ((v - xMin) / (xMax - xMin)) * (W - pad.l - pad.r);
    const sy = v => H - pad.b - ((v - xMin) / (xMax - xMin)) * (H - pad.t - pad.b);
    const hit = rows.find(r => Math.hypot(mx - sx(r.avgRealized), my - sy(r.implied)) <= 10);
    setTip(hit ? { ...hit, mx, my } : null);
  };

  return (
    <div style={{ position: "relative", width: "100%", height: 280 }}>
      <canvas
        ref={ref}
        style={{ width: "100%", height: "100%", cursor: "crosshair" }}
        onMouseMove={onMouseMove}
        onMouseLeave={() => setTip(null)}
        onClick={() => tip && onPick && onPick(tip.t)}
      />
      {tip && (
        <div style={{ position: "absolute", left: Math.min(tip.mx + 14, 1000), top: Math.max(8, tip.my - 50), background: "#0f172a", color: "#fff", padding: "8px 10px", borderRadius: 6, fontSize: 11, fontFamily: "monospace", pointerEvents: "none", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{tip.t}</div>
          <div>Implied: <strong>{tip.implied.toFixed(2)}%</strong></div>
          <div>Realized: <strong>{tip.avgRealized.toFixed(2)}%</strong></div>
          <div>Mispricing: <strong>{tip.mispricing >= 0 ? "+" : ""}{tip.mispricing.toFixed(2)} pp</strong></div>
        </div>
      )}
    </div>
  );
}

export default function EarningsEdgeTab() {
  const [weekOffset, setWeekOffset] = useState(0);
  const [demo, setDemo]             = useState(true);
  const [liveErr, setLiveErr]       = useState("");
  const [liveMessage, setLiveMessage] = useState("");
  const [liveLoading, setLiveLoading] = useState(false);
  const [liveCalendar, setLiveCalendar] = useState([]);   // Real Finnhub calendar entries for the current week
  const [selectedT, setSelectedT]   = useState("NVDA");
  const [watchlist, setWatchlist]   = useState(DEFAULT_WATCHLIST);
  const [tickerInput, setTickerInput] = useState("");

  // ── Load Vault watchlist on mount (with localStorage cache) ──
  useEffect(() => {
    const API = process.env.REACT_APP_BACKEND_URL || "";
    const saved = localStorage.getItem(WATCHLIST_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed) && parsed.length > 0) {
          setWatchlist(parsed);
          return;
        }
      } catch { /* fall through */ }
    }
    fetch(`${API}/api/trading/vault`, { credentials: "include" })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        const tickers = (list || []).map(s => s && s.ticker).filter(Boolean);
        const next = tickers.length > 0 ? tickers : DEFAULT_WATCHLIST;
        setWatchlist(next);
        try { localStorage.setItem(WATCHLIST_KEY, JSON.stringify(next)); } catch { /* noop */ }
      })
      .catch(() => setWatchlist(DEFAULT_WATCHLIST));
  }, []);

  const addTicker = useCallback(() => {
    const t = (tickerInput || "").toUpperCase().trim();
    if (!t || watchlist.includes(t)) { setTickerInput(""); return; }
    const next = [...watchlist, t];
    setWatchlist(next);
    setTickerInput("");
    try { localStorage.setItem(WATCHLIST_KEY, JSON.stringify(next)); } catch { /* noop */ }
  }, [tickerInput, watchlist]);

  const removeTicker = useCallback((t) => {
    const next = watchlist.filter(x => x !== t);
    setWatchlist(next);
    try { localStorage.setItem(WATCHLIST_KEY, JSON.stringify(next)); } catch { /* noop */ }
  }, [watchlist]);

  // ── LOAD LIVE: hit backend /api/earnings/live with Monday-Friday of
  // the currently-viewed week (driven by weekOffset). Key sits server-
  // side in FINNHUB_API_KEY — frontend never handles it. On success
  // with a non-empty calendar: switch to live mode, cache the entries,
  // and surface them through `rows` (filtered against the watchlist).
  // On empty calendar: show the spec-mandated message + stay in demo.
  // On any error: keep demo on and surface a small inline message.
  const loadLive = useCallback(async () => {
    setLiveErr("");
    setLiveMessage("");
    setLiveLoading(true);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || "";
      // Compute Monday-Friday of the target week. JS Date#getDay() returns
      // 0 (Sun) through 6 (Sat); the conditional makes Sunday roll back to
      // the previous Monday rather than forward to the next one.
      const today  = new Date();
      const monday = new Date(today);
      monday.setDate(today.getDate() - (today.getDay() === 0 ? 6 : today.getDay() - 1) + weekOffset * 7);
      const friday = new Date(monday);
      friday.setDate(monday.getDate() + 4);
      const from = monday.toISOString().slice(0, 10);
      const to   = friday.toISOString().slice(0, 10);
      const r = await fetch(`${API}/api/earnings/live?from_date=${from}&to_date=${to}`, { credentials: "include" });
      if (!r.ok) {
        const detail = r.status === 503
          ? "FINNHUB_API_KEY not set on backend"
          : `Finnhub error (${r.status})`;
        throw new Error(detail);
      }
      const data     = await r.json();
      const calendar = Array.isArray(data?.earningsCalendar) ? data.earningsCalendar : [];
      if (calendar.length === 0) {
        // Spec-mandated empty-week message; UI stays in demo mode.
        setLiveMessage("No qualifying earnings this week. Try a different week.");
        setLiveCalendar([]);
        setDemo(true);
        return;
      }
      setLiveCalendar(calendar);
      setDemo(false);
    } catch (e) {
      setLiveErr(e.message || "Failed to load live earnings");
      setLiveCalendar([]);
      setDemo(true);
    } finally {
      setLiveLoading(false);
    }
  }, [weekOffset]);

  // ── When the user changes weeks while in LIVE mode, automatically
  // re-fetch the calendar for the new window. Only triggers in live
  // mode so demo mode stays snappy and offline-friendly.
  useEffect(() => {
    if (!demo) loadLive();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [weekOffset]);

  // Live spot prices (will populate when Polygon proxy is reachable)
  usePrices(watchlist);
  // Earnings hook — fetch but render is independent (used as a soft hint)
  useVaultEarnings(watchlist);

  // ── rows derivation: in live mode, build each row from a real
  // Finnhub calendar entry (filtered by the user's watchlist). The
  // analytical math (avg realized move, implied, mispricing) still
  // uses the seeded buildDemoRow because the free Finnhub tier
  // doesn't return historical post-earnings price moves — but the
  // ticker AND the report day come from real Finnhub data.
  // Spec #6: if no watchlist tickers match the calendar, fall back
  // to showing every ticker the API returned so the panel never
  // sits blank in live mode.
  const rows = useMemo(() => {
    if (!demo && liveCalendar.length > 0) {
      const matched = liveCalendar.filter(e => watchlist.includes(e.symbol));
      const source  = matched.length > 0 ? matched : liveCalendar;
      return source
        .map((e, i) => buildDemoRow(
          watchlist.indexOf(e.symbol) >= 0 ? watchlist.indexOf(e.symbol) : i,
          weekOffset,
          e.symbol,
          e.date,
        ))
        .sort((a, b) => Math.abs(b.mispricing) - Math.abs(a.mispricing));
    }
    return buildDemo(weekOffset, watchlist);
  }, [demo, liveCalendar, weekOffset, watchlist]);
  const hero = rows[0];
  const sel  = rows.find(r => r.t === selectedT) || rows[0];

  // Keep selectedT in sync with the current watchlist
  useEffect(() => {
    if (rows.length && !rows.find(r => r.t === selectedT)) {
      setSelectedT(rows[0].t);
    }
  }, [rows, selectedT]);

  const maxAbs   = Math.max(...rows.map(r => Math.abs(r.mispricing)), 1);
  const weeks    = [
    { offset: -1, label: "LAST WEEK" },
    { offset:  0, label: "THIS WEEK" },
    { offset:  1, label: "NEXT WEEK" },
    { offset:  2, label: "+2" },
  ];

  const smallBtn = { background: C.indigo, color: "#fff", border: "none", borderRadius: 6, padding: "4px 10px", fontSize: 11, fontWeight: 600, cursor: "pointer" };

  return (
    <div data-testid="earnings-edge-tab" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* ── Header bar ─────────────────────────────────────────── */}
      <div style={{ ...CARD, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 12, padding: "12px 16px" }}>
        <div>
          <div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 14, color: "#111", letterSpacing: "0.05em" }}>EARNINGS EDGE</div>
          <div style={{ ...SECTION_HEADER, marginTop: 2 }}>Implied vs Realized · Ranked by mispricing</div>
        </div>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {weeks.map(w => (
            <button
              key={w.offset}
              data-testid={`week-${w.offset}`}
              onClick={() => setWeekOffset(w.offset)}
              style={{
                background: weekOffset === w.offset ? C.indigo : "#fff",
                color: weekOffset === w.offset ? "#fff" : C.dim,
                border: weekOffset === w.offset ? `1px solid ${C.indigo}` : "1px solid #e2e8f0",
                borderRadius: 999, padding: "5px 12px", fontSize: 11, fontWeight: 600, cursor: "pointer", letterSpacing: "0.05em",
              }}
            >{w.label}</button>
          ))}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <button
            data-testid="demo-toggle"
            onClick={() => setDemo(d => !d)}
            style={{
              background: demo ? C.ab : C.gb,
              color: demo ? C.amber : C.green,
              border: `1px solid ${demo ? C.amber : C.green}`,
              borderRadius: 999, padding: "4px 12px", fontSize: 10, fontWeight: 700, cursor: "pointer", letterSpacing: "0.06em",
            }}>{demo ? "DEMO ON" : "LIVE"}</button>
          <button
            data-testid="finnhub-load-btn"
            onClick={loadLive}
            disabled={liveLoading}
            style={{ background: liveLoading ? C.muted : C.indigo, color: "#fff", border: "none", borderRadius: 6, padding: "5px 12px", fontSize: 11, fontWeight: 600, cursor: liveLoading ? "not-allowed" : "pointer" }}
          >{liveLoading ? "LOADING…" : "LOAD LIVE"}</button>
          {liveErr && (
            <span data-testid="ee-live-error" style={{ fontSize: 10, color: C.red, fontWeight: 600 }}>{liveErr}</span>
          )}
          {liveMessage && (
            <span data-testid="ee-live-message" style={{ fontSize: 10, color: C.amber, fontWeight: 600 }}>{liveMessage}</span>
          )}
        </div>
      </div>

      {/* ── Watchlist chip bar ─────────────────────────────────── */}
      <div data-testid="ee-watchlist" style={{ ...CARD, padding: "10px 14px", display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{ ...SECTION_HEADER, marginRight: 4 }}>Watchlist</div>
        {watchlist.map(t => (
          <span key={t} data-testid={`ee-chip-${t}`} style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 999,
            padding: "3px 4px 3px 10px", fontSize: 11, fontFamily: "monospace", fontWeight: 600, color: "#111",
          }}>
            {t}
            <button
              onClick={() => removeTicker(t)} data-testid={`ee-remove-${t}`}
              title={`Remove ${t}`}
              style={{ background: "transparent", border: "none", color: C.muted, cursor: "pointer", fontSize: 14, lineHeight: 1, padding: "0 4px" }}
            >×</button>
          </span>
        ))}
        <input
          data-testid="ee-add-ticker-input"
          value={tickerInput}
          onChange={(e) => setTickerInput(e.target.value.toUpperCase())}
          onKeyDown={(e) => { if (e.key === "Enter") addTicker(); }}
          placeholder="Add ticker"
          style={{ ...inputStyle, width: 100, fontFamily: "monospace", textTransform: "uppercase" }}
        />
        <button data-testid="ee-add-ticker-btn" onClick={addTicker} style={smallBtn}>Add</button>
      </div>

      {/* ── Demo banner ────────────────────────────────────────── */}
      {demo && (
        <div data-testid="demo-banner" style={{ background: C.ab, border: `1px solid ${C.amber}`, color: C.amber, borderRadius: 8, padding: "10px 16px", fontSize: 12, fontWeight: 500 }}>
          DEMO MODE · Synthetic data for illustration. Enter a Finnhub API key above to load live earnings.
        </div>
      )}

      {/* ── Hero card ──────────────────────────────────────────── */}
      <div style={{ ...CARD, padding: "20px 24px", display: "grid", gridTemplateColumns: "minmax(140px, 1fr) 2fr 1fr", gap: 24, alignItems: "center" }}>
        <div>
          <div style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 48, color: "#111", lineHeight: 1 }}>{hero.t}</div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>{hero.name}</div>
          <div style={{ fontSize: 10, color: C.muted, marginTop: 2 }}>Reports {hero.reportDay}</div>
        </div>
        <div>
          <div style={{ display: "flex", alignItems: "baseline", gap: 18, flexWrap: "wrap" }}>
            <div>
              <div style={SECTION_HEADER}>Implied move</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: hero.mispricing > 0 ? C.red : C.green, fontFamily: "monospace" }}>{hero.implied.toFixed(1)}%</div>
            </div>
            <div style={{ fontSize: 12, color: C.muted }}>vs</div>
            <div>
              <div style={SECTION_HEADER}>Avg realized (8Q)</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: C.navy, fontFamily: "monospace" }}>{hero.avgRealized.toFixed(1)}%</div>
            </div>
          </div>
          <div style={{ marginTop: 10 }}><MispricingBadge pp={hero.mispricing} /></div>
          <div style={{ marginTop: 12 }}><HistoSparkline data={hero.realized} avg={hero.avgRealized} w={180} h={42} /></div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <div style={{ background: "#f9fafb", borderRadius: 7, padding: "10px 12px" }}>
            <div style={SECTION_HEADER}>Std deviation</div>
            <div style={{ fontSize: 20, fontWeight: 700, color: C.navy, fontFamily: "monospace" }}>{hero.stdDev.toFixed(2)}</div>
          </div>
          <div style={{ background: "#f9fafb", borderRadius: 7, padding: "10px 12px" }}>
            <div style={SECTION_HEADER}>Implied percentile</div>
            <div style={{ fontSize: 20, fontWeight: 700, fontFamily: "monospace", color: hero.percentile > 75 ? C.red : hero.percentile < 25 ? C.green : C.navy }}>{hero.percentile}th</div>
          </div>
        </div>
      </div>

      {/* ── Two-column: leaderboard + detail ───────────────────── */}
      <div className="ee-grid" style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 16 }}>
        {/* Leaderboard */}
        <div style={CARD}>
          <div style={{ ...SECTION_HEADER, marginBottom: 10 }}>All earnings this week — ranked by mispricing</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {rows.map((r) => {
              const selected = r.t === selectedT;
              const widthPct = (Math.abs(r.mispricing) / maxAbs) * 46;
              const over     = r.mispricing > 0;
              return (
                <div
                  key={r.t}
                  data-testid={`leader-row-${r.t}`}
                  onClick={() => setSelectedT(r.t)}
                  style={{
                    display: "flex", alignItems: "center", gap: 8, padding: "6px 8px",
                    borderRadius: 6, cursor: "pointer",
                    background: selected ? "rgba(79,70,229,0.04)" : "transparent",
                    borderLeft: selected ? `2px solid ${C.indigo}` : "2px solid transparent",
                    fontFamily: "monospace",
                  }}
                  onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = "#f8fafc"; }}
                  onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = "transparent"; }}
                >
                  <div style={{ width: 60, fontWeight: 700, color: "#111", fontSize: 12 }}>{r.t}</div>
                  <div style={{ flex: 1, position: "relative", height: 14, background: "transparent" }}>
                    <div style={{ position: "absolute", left: "50%", top: 0, bottom: 0, width: 1, background: "#e2e8f0" }} />
                    <div style={{
                      position: "absolute", top: 2, bottom: 2,
                      left: over ? "50%" : `${50 - widthPct}%`,
                      width: `${widthPct}%`,
                      background: over ? "rgba(220,38,38,0.6)" : "rgba(22,163,74,0.6)",
                      borderRadius: 2,
                    }} />
                  </div>
                  <div style={{ width: 60, textAlign: "right", fontSize: 11, fontWeight: 600, color: over ? C.red : C.green }}>
                    {over ? "+" : ""}{r.mispricing.toFixed(1)} pp
                  </div>
                  <div style={{ width: 32, textAlign: "right", fontSize: 10, color: C.muted }}>{r.reportDay}</div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Detail */}
        <div style={CARD}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
            <div>
              <div style={{ fontFamily: "monospace", fontSize: 22, fontWeight: 700, color: "#111" }}>{sel.t}</div>
              <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{sel.name}</div>
              <div style={{ fontSize: 10, color: C.muted }}>Reports {sel.reportDay}</div>
            </div>
            <MispricingBadge pp={sel.mispricing} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
            <div style={{ background: "#f9fafb", borderRadius: 7, padding: "8px 10px" }}>
              <div style={SECTION_HEADER}>Current implied</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: sel.mispricing > 0 ? C.red : C.green, fontFamily: "monospace" }}>{sel.implied.toFixed(2)}%</div>
            </div>
            <div style={{ background: "#f9fafb", borderRadius: 7, padding: "8px 10px" }}>
              <div style={SECTION_HEADER}>Avg realized</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.navy, fontFamily: "monospace" }}>{sel.avgRealized.toFixed(2)}%</div>
            </div>
            <div style={{ background: "#f9fafb", borderRadius: 7, padding: "8px 10px" }}>
              <div style={SECTION_HEADER}>Std deviation</div>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.navy, fontFamily: "monospace" }}>{sel.stdDev.toFixed(2)}</div>
            </div>
            <div style={{ background: "#f9fafb", borderRadius: 7, padding: "8px 10px" }}>
              <div style={SECTION_HEADER}>Implied percentile</div>
              <div style={{ fontSize: 17, fontWeight: 700, fontFamily: "monospace", color: sel.percentile > 75 ? C.red : sel.percentile < 25 ? C.green : C.navy }}>{sel.percentile}th</div>
            </div>
          </div>
          <div style={{ marginTop: 14, position: "relative", padding: "8px 0" }}>
            <DetailBars data={sel.realized} implied={sel.implied} avg={sel.avgRealized} />
          </div>
          <div style={{ fontSize: 12, color: C.dim, lineHeight: 1.6, marginTop: 10 }}>
            Options are pricing in a <strong style={{ color: sel.mispricing > 0 ? C.red : C.green }}>{Math.abs(sel.mispricing).toFixed(1)} pp</strong> {sel.mispricing > 0 ? "premium over" : "discount to"} the avg realized 8-quarter move.{" "}
            {sel.mispricing > 5  ? "Premium-selling setups (iron condors, short strangles) may have edge — but check IV percentile + earnings rule first." :
              sel.mispricing < -5 ? "Premium-buying setups (long straddles) may have edge — but watch slippage on the entry." :
              "Pricing is near-fair — no statistical edge in directionless plays."}
          </div>
        </div>
      </div>

      {/* ── Scatter plot ───────────────────────────────────────── */}
      <div style={CARD}>
        <div style={{ ...SECTION_HEADER, marginBottom: 6 }}>
          Implied vs Realized scatter — dots above line = overpriced (red), below = underpriced (green)
        </div>
        <ScatterPlot rows={rows} onPick={setSelectedT} />
      </div>

      {/* Responsive collapse */}
      <style>{`
        @media (max-width: 768px) {
          .ee-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

/* Detail panel bar chart — 8 quarters with implied reference line. */
function DetailBars({ data, implied, avg }) {
  const max = Math.max(...data, implied) * 1.1;
  const W   = 100;
  const H   = 110;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ width: "100%", height: 110, display: "block" }}>
      {data.map((v, i) => {
        const bw = (W - 16) / data.length;
        const x  = 8 + i * bw;
        const bh = (v / max) * (H - 18);
        const y  = H - 12 - bh;
        const above = v > avg;
        return (
          <g key={i}>
            <rect x={x + 1} y={y} width={bw - 2} height={bh} fill={above ? "rgba(22,163,74,0.55)" : "rgba(148,163,184,0.5)"} rx={0.6} />
            <text x={x + bw / 2} y={H - 2} fontSize="3" textAnchor="middle" fill="#94a3b8" fontFamily="monospace">Q-{8 - i}</text>
          </g>
        );
      })}
      <line x1="6" x2={W - 6} y1={H - 12 - (implied / max) * (H - 18)} y2={H - 12 - (implied / max) * (H - 18)} stroke="#dc2626" strokeDasharray="2 1.5" strokeWidth="0.6" />
      <text x={W - 26} y={H - 12 - (implied / max) * (H - 18) - 1.5} fontSize="3" fill="#dc2626" fontFamily="monospace">Implied {implied.toFixed(1)}%</text>
    </svg>
  );
}
